import {
  type ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  type TableOptions,
  type TableState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import { useMemo } from "react";

export interface ReactTableOptions<TData extends { id: string }, TValue>
  extends Omit<Partial<TableOptions<TData>>, "columns" | "data"> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}
export interface DataTableOptions<TData extends { id: string }> {
  selectedRows: TData[];
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  /**
   * Column visibility is always controlled — own the state with
   * `useColumnVisibility` in whichever component renders the columns filter.
   */
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (columnVisibility: VisibilityState) => void;
}

export default function useDataTable<TData extends { id: string }, TValue>(
  reactTableOptions: ReactTableOptions<TData, TValue>,
  {
    selectedRows,
    onRowSelectionChange,
    columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
  }: DataTableOptions<TData>,
) {
  const { data } = reactTableOptions;

  const isRowSelectionEnabled = onRowSelectionChange !== undefined;

  const rowSelection = useMemo(() => {
    return selectedRows.reduce(
      (acc, { id }) => {
        acc[id] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [isRowSelectionEnabled, selectedRows]);

  let state: Partial<TableState> = {
    rowSelection,
    columnVisibility,
  };

  if (reactTableOptions.state) {
    state = { ...state, ...reactTableOptions.state };
  }
  const tableOptions: TableOptions<TData> = {
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: (updaterOrValue) => {
      const newVisibilityState =
        typeof updaterOrValue === "function"
          ? updaterOrValue(columnVisibility)
          : updaterOrValue;

      setColumnVisibility(newVisibilityState);
    },
    enableRowSelection: isRowSelectionEnabled,
    ...(isRowSelectionEnabled && {
      onRowSelectionChange: (updater) => {
        const newSelection =
          typeof updater === "function" ? updater(rowSelection) : updater;

        const updatedSelectedRows = [
          ...selectedRows.filter(({ id }) => newSelection[id]),
          ...data.filter(
            ({ id }) =>
              newSelection[id] &&
              !selectedRows.find((selectedRow) => selectedRow.id === id),
          ),
        ];
        onRowSelectionChange(updatedSelectedRows);
      },
    }),
    manualPagination: true,
    ...reactTableOptions,
    state,
  };

  return useReactTable(tableOptions);
}
